import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Mail, Plus, Trash2, Edit2, X, Save, Send, Users, Filter, CheckSquare, Link2, Loader2, FileText, ShieldCheck, RotateCcw, MailQuestion, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from './RichTextEditor';
import SenderHealthCard from './SenderHealthCard';

const API = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = [
  'Oil & Gas', 'Wastewater', 'Water Treatment', 'Power Generation',
  'Manufacturing', 'Marine', 'Chemical', 'Pulp & Paper', 'Mining',
  'Food & Beverage', 'Government', 'Construction', 'Vendor', 'Other'
];

const TEMPLATES = {
  newsletter: { name: 'Newsletter', subject: 'BCS Monthly Newsletter', content: '<h2>BCS Infrastructure Update</h2><p>Dear valued client,</p><p>Here are the latest updates from BCS:</p><ul><li>New project completions</li><li>Industry insights</li><li>Upcoming services</li></ul><p>Best regards,<br>BCS Team</p>' },
  promotion: { name: 'Promotion', subject: 'Special Offer from BCS', content: '<h2>Limited Time Offer</h2><p>We\'re excited to offer you exclusive pricing on our coating and repair services.</p><p><strong>Contact us today</strong> to schedule a free assessment.</p>' },
  welcome: { name: 'Welcome', subject: 'Welcome to BCS', content: '<h2>Welcome!</h2><p>Thank you for connecting with BCS. We provide nationwide general contracting services for infrastructure.</p><p>Our team is ready to help with your next project.</p>' },
};


/* -----------------------------------------------------------
 * ImageSwapPanel
 * Extracts Unsplash image URLs from the email HTML and lets
 * the user swap each one for a fresh unique pick via the
 * /api/crm/campaigns/swap-image endpoint.
 * ----------------------------------------------------------- */
const ImageSwapPanel = ({ content, onReplace }) => {
  // Extract unique stock image URLs (Pexels, plus legacy Unsplash for old campaigns)
  const urls = React.useMemo(() => {
    if (!content) return [];
    const re = /https:\/\/images\.(pexels|unsplash)\.com\/[^"'\s)]+/g;
    return Array.from(new Set((content.match(re) || [])));
  }, [content]);

  const [swapping, setSwapping] = useState(null); // index being swapped
  const [queries, setQueries] = useState({}); // { idx: query string }

  if (urls.length === 0) return null;

  const swap = async (idx, url) => {
    const q = (queries[idx] || 'industrial infrastructure').trim();
    setSwapping(idx);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/swap-image`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ old_url: url, query: q }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Swap failed');
      onReplace(url, data.url);
      toast.success(`Image ${idx + 1} swapped — fresh unique pick`);
    } catch (err) {
      toast.error(err.message || 'Swap failed');
    } finally {
      setSwapping(null);
    }
  };

  return (
    <div className="border border-amber-200 rounded-lg bg-amber-50/40 p-3 space-y-2" data-testid="image-swap-panel">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-amber-900 uppercase tracking-wide">Images in this campaign ({urls.length})</span>
        <span className="text-xs text-gray-600">— don't like one? Swap it for a fresh never-used Unsplash pick</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {urls.map((url, idx) => (
          <div key={url} className="border rounded bg-white p-2 space-y-1.5" data-testid={`swap-card-${idx}`}>
            <img src={url} alt={`Campaign image ${idx + 1}`} className="w-full h-20 object-cover rounded" />
            <input
              type="text"
              placeholder="Search query (e.g. refinery)"
              value={queries[idx] || ''}
              onChange={e => setQueries(q => ({ ...q, [idx]: e.target.value }))}
              className="w-full px-2 py-1 text-xs border rounded focus:ring-1 focus:ring-amber-500 focus:outline-none"
              data-testid={`swap-query-${idx}`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-full h-7 text-xs border-amber-600 text-amber-700 hover:bg-amber-100"
              onClick={() => swap(idx, url)}
              disabled={swapping !== null}
              data-testid={`swap-btn-${idx}`}
            >
              {swapping === idx ? <><Loader2 size={12} className="inline mr-1 animate-spin" /> Swapping…</> : <>🔄 Swap #{idx + 1}</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

const CRMCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', subject: '', content: '' });

  // Send modal state
  const [sendModal, setSendModal] = useState(null); // campaign id
  const [sendMode, setSendMode] = useState('all');
  const [sendIndustry, setSendIndustry] = useState('');
  const [contacts, setContacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [sending, setSending] = useState(false);

  // Import-from-URL state
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);

  // Convert-blog-to-email state
  const [showFromBlog, setShowFromBlog] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [selectedBlogSlug, setSelectedBlogSlug] = useState('');
  const [convertingBlog, setConvertingBlog] = useState(false);

  // Campaign editor state
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  // Opt-In template editor state
  const [showOptInTpl, setShowOptInTpl] = useState(false);
  const [optInTpl, setOptInTpl] = useState({ subject: '', content: '' });
  const [optInTplLoaded, setOptInTplLoaded] = useState(false);
  const [optInTplLoading, setOptInTplLoading] = useState(false);
  const [optInTplSaving, setOptInTplSaving] = useState(false);
  const [optInTplHtmlMode, setOptInTplHtmlMode] = useState(false);
  const [optInTplSending, setOptInTplSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/campaigns?limit=100`, { credentials: 'include' });
      setCampaigns((await res.json()).campaigns);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const url = editing ? `${API}/api/crm/campaigns/${editing}` : `${API}/api/crm/campaigns`;
      const res = await fetch(url, { method: editing ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(editing ? 'Updated' : 'Created');
      setShowForm(false); setEditing(null); await load();
    } catch (err) { toast.error(err.message); }
  };

  // Open send modal
  const openSendModal = async (id) => {
    setSendModal(id);
    setSendMode('all');
    setSendIndustry('');
    setSelectedIds([]);
    setLoadingContacts(true);
    try {
      const res = await fetch(`${API}/api/crm/contacts?limit=1000`, { credentials: 'include' });
      setContacts((await res.json()).contacts);
    } catch { toast.error('Failed to load contacts'); }
    finally { setLoadingContacts(false); }
  };

  const handleSend = async () => {
    const body = { mode: sendMode };
    if (sendMode === 'industry') body.industry = sendIndustry;
    if (sendMode === 'selected') body.contact_ids = selectedIds;

    if (sendMode === 'industry' && !sendIndustry) { toast.error('Select an industry'); return; }
    if (sendMode === 'selected' && selectedIds.length === 0) { toast.error('Select at least one contact'); return; }

    const count = sendMode === 'all' ? contacts.length
      : sendMode === 'opted_in' ? contacts.filter(c => c.opt_in_status === 'opted_in' && !c.excluded).length
      : sendMode === 'industry' ? contacts.filter(c => (c.industry || '').toLowerCase() === sendIndustry.toLowerCase()).length
      : selectedIds.length;

    if (!window.confirm(`Send this campaign to ${count} contact(s)?`)) return;

    setSending(true);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/${sendModal}/send`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Sent to ${data.sent_count}/${data.recipients} recipients`);
      if (data.errors?.length) toast.error(`${data.errors.length} delivery errors`);
      setSendModal(null);
      await load();
    } catch (err) { toast.error(err.message || 'Send failed'); }
    finally { setSending(false); }
  };

  const handleTestEmail = async (campaignId) => {
    const email = window.prompt('Send test email to:', 'jasonkraft@bcs-us.com');
    if (!email) return;
    const campaign = campaigns.find(c => c.id === campaignId);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/test-email`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_email: email,
          subject: campaign?.subject || 'Test',
          content: campaign?.content || '<p>Test</p>',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(`Test email sent to ${email}!`);
    } catch (err) { toast.error(err.message || 'Test send failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`${API}/api/crm/campaigns/${id}`, { credentials: 'include', method: 'DELETE' });
    toast.success('Deleted'); await load();
  };

  const applyTemplate = (key) => {
    const t = TEMPLATES[key];
    setForm(f => ({ ...f, subject: t.subject, content: t.content }));
  };

  const handleImportFromUrl = async () => {
    const url = (importUrl || '').trim();
    if (!url) { toast.error('Paste the email "View in Browser" URL first'); return; }
    if (!/^https?:\/\//i.test(url)) { toast.error('URL must start with http:// or https://'); return; }
    setImporting(true);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/import-from-url`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, redirect_to: 'https://bcs-us.com' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      // Open the create form pre-filled with the cleaned content
      setEditing(null);
      setForm({
        name: data.subject ? `AI: ${data.subject.slice(0, 80)}` : 'AI-Rebranded Email',
        subject: data.subject || '',
        content: data.content || '',
      });
      setShowImport(false);
      setImportUrl('');
      setShowForm(true);
      const s = data.stats || {};
      toast.success(`AI rebrand complete! Generated ${s.sections_generated || 0} sections + ${s.bullets_generated || 0} bullets. Review below.`);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const startNewFieldReport = () => {
    // Navigate to the form-driven Field Report Builder page
    window.location.href = '/admin/field-report/new';
  };


  const openBlogPicker = async () => {
    setShowFromBlog(true);
    setSelectedBlogSlug('');
    if (blogPosts.length === 0) {
      try {
        const res = await fetch(`${API}/api/blog/posts?limit=100`);
        const data = await res.json();
        setBlogPosts(data.posts || []);
      } catch (err) {
        toast.error('Failed to load blog posts');
      }
    }
  };

  const handleConvertFromBlog = async () => {
    if (!selectedBlogSlug) { toast.error('Pick a blog post first'); return; }
    setConvertingBlog(true);
    try {
      const res = await fetch(`${API}/api/crm/campaigns/from-blog`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: selectedBlogSlug }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Conversion failed');
      setEditing(null);
      setForm({
        name: data.subject ? `Blog: ${data.subject.slice(0, 80)}` : 'Blog Email',
        subject: data.subject || '',
        content: data.content || '',
      });
      setShowFromBlog(false);
      setSelectedBlogSlug('');
      setShowForm(true);
      const s = data.stats || {};
      toast.success(`Blog post converted! ${s.sections_generated || 0} sections + ${s.bullets_generated || 0} bullets. Review below.`);
    } catch (err) {
      toast.error(err.message || 'Conversion failed');
    } finally {
      setConvertingBlog(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // ── Opt-In Template handlers ─────────────────────────────
  const openOptInTpl = async () => {
    setShowOptInTpl(true);
    ensureContactsLoadedForOptIn();
    loadOptInHealth();
    if (optInTplLoaded) return;
    setOptInTplLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/opt-in-template`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load template');
      setOptInTpl({ subject: data.subject || '', content: data.content || '' });
      setOptInTplLoaded(true);
    } catch (err) {
      toast.error(err.message || 'Failed to load opt-in template');
    } finally {
      setOptInTplLoading(false);
    }
  };

  const handleSaveOptInTpl = async () => {
    setOptInTplSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/opt-in-template`, { credentials: 'include',
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: optInTpl.subject, content: optInTpl.content }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setOptInTpl({ subject: data.subject || '', content: data.content || '' });
      toast.success('Opt-in template saved');
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setOptInTplSaving(false);
    }
  };

  const handleResetOptInTpl = async () => {
    if (!window.confirm('Restore the opt-in template to BCS defaults? This overwrites your edits.')) return;
    setOptInTplSaving(true);
    try {
      const res = await fetch(`${API}/api/crm/opt-in-template/reset`, { credentials: 'include', method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setOptInTpl({ subject: data.subject || '', content: data.content || '' });
      toast.success('Opt-in template reset to defaults');
    } catch (err) {
      toast.error(err.message || 'Reset failed');
    } finally {
      setOptInTplSaving(false);
    }
  };

  const handleTestOptIn = async () => {
    const email = window.prompt('Send a TEST opt-in email to this address:', 'jasonkraft@bcs-us.com');
    if (!email) return;
    // Save first so the test uses the latest edits
    await handleSaveOptInTpl();
    setOptInTplSending(true);
    try {
      const res = await fetch(`${API}/api/crm/contacts/send-opt-in`, { credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_only: true, test_email: email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Send failed');
      toast.success(`Test opt-in email sent to ${email}`);
    } catch (err) {
      toast.error(err.message || 'Test send failed');
    } finally {
      setOptInTplSending(false);
    }
  };

  const [optInSendMode, setOptInSendMode] = useState('all'); // 'all' | 'industry' | 'selected' | 'remaining'
  const [optInIndustry, setOptInIndustry] = useState('');
  const [optInSelectedIds, setOptInSelectedIds] = useState([]);
  const [optInContactsLoaded, setOptInContactsLoaded] = useState(false);
  const [optInForceSend, setOptInForceSend] = useState(false);
  const [optInHealth, setOptInHealth] = useState(null); // {risk_level, risk_reasons[]}
  const [optInHealthLoading, setOptInHealthLoading] = useState(false);

  const loadOptInHealth = useCallback(async () => {
    setOptInHealthLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/sender-health?days=30`, { credentials: 'include' });
      if (!res.ok) { setOptInHealth(null); return; }
      const data = await res.json();
      setOptInHealth({ risk_level: data.risk_level, risk_reasons: data.risk_reasons || [], sample_size: data.sample_size, min_sample: data.min_sample_for_risk });
    } catch { setOptInHealth(null); }
    finally { setOptInHealthLoading(false); }
  }, []);

  const ensureContactsLoadedForOptIn = async () => {
    if (optInContactsLoaded) return;
    setLoadingContacts(true);
    try {
      const res = await fetch(`${API}/api/crm/contacts?limit=2000`, { credentials: 'include' });
      const data = await res.json();
      setContacts(data.contacts || []);
      setOptInContactsLoaded(true);
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleSendOptInBlast = async () => {
    // Validate mode
    if (optInSendMode === 'industry' && !optInIndustry) {
      toast.error('Pick an industry first'); return;
    }
    if (optInSendMode === 'selected' && optInSelectedIds.length === 0) {
      toast.error('Select at least one contact'); return;
    }

    // Build recipient count for the confirm message
    const eligibleContacts = (contacts || []).filter(c => !c.excluded
      && (!c.opt_in_status || c.opt_in_status === '' || c.opt_in_status === 'pending'));
    const neverReceived = (contacts || []).filter(c => !c.excluded
      && (!c.opt_in_status || c.opt_in_status === ''));
    const count = optInSendMode === 'all' ? eligibleContacts.length
      : optInSendMode === 'remaining' ? neverReceived.length
      : optInSendMode === 'industry' ? eligibleContacts.filter(c => (c.industry || '').toLowerCase() === optInIndustry.toLowerCase()).length
      : optInSelectedIds.length;

    const label = optInSendMode === 'all' ? 'ALL active contacts that have not yet responded'
      : optInSendMode === 'remaining' ? 'contacts who NEVER received the opt-in email (safe re-send, no duplicates)'
      : optInSendMode === 'industry' ? `contacts in industry "${optInIndustry}"`
      : `${optInSelectedIds.length} selected contact(s)`;

    // Warn the user if sender health is critical and they did NOT pre-authorize the override
    if (optInHealth?.risk_level === 'critical' && !optInForceSend) {
      const reasons = (optInHealth.risk_reasons || []).map(r => '• ' + r).join('\n');
      toast.error(`Sender reputation is CRITICAL — blast blocked.\n${reasons}\n\nTo send anyway, check "Override critical block (force send)" and click Send Opt-In Blast again.`, { duration: 12000 });
      return;
    }

    const confirmMsg = `Send the Opt-In email to ${label}?\n\nEstimated ${count} recipient(s).${optInForceSend ? '\n\n⚠️ FORCE SEND is ON — sender-health critical block will be overridden.' : ''}\n\nThis cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    await handleSaveOptInTpl();
    setOptInTplSending(true);
    try {
      const payload = { test_only: false, mode: optInSendMode, force: !!optInForceSend };
      if (optInSendMode === 'industry') payload.industry = optInIndustry;
      if (optInSendMode === 'selected') payload.contact_ids = optInSelectedIds;

      const res = await fetch(`${API}/api/crm/contacts/send-opt-in`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // Read body ONCE as text, then parse safely — eliminates any "body already read" risk
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = { detail: raw || 'Empty response' }; }

      if (res.status === 409) {
        const reasons = (data.detail?.risk_reasons || []).map(r => '• ' + r).join('\n');
        toast.error(`Sender reputation is CRITICAL — blast blocked by server.\n${reasons}\n\nTo override, check "Override critical block (force send)" above and try again.`, { duration: 14000 });
        await loadOptInHealth(); // refresh the inline status
        return;
      }
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail : (data.detail?.message || `Send failed (HTTP ${res.status})`);
        throw new Error(msg);
      }
      if (data.status === 'no_recipients' || !data.job_id) {
        toast.info('No matching contacts to send to.');
        return;
      }

      toast.success(`Blast started: ${data.total} contacts queued. Sending in background…`);
      const jobId = data.job_id;
      const startedAt = Date.now();

      // Poll job progress every 3 seconds until completed (or 15-min hard ceiling)
      let lastSent = 0;
      while (Date.now() - startedAt < 15 * 60 * 1000) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const jres = await fetch(`${API}/api/crm/contacts/send-opt-in/jobs/${jobId}`, { credentials: 'include' });
          if (!jres.ok) continue;
          const job = await jres.json();
          if (job.sent !== lastSent) {
            toast.success(`Progress: ${job.sent}/${job.total} sent`, { id: `blast-${jobId}` });
            lastSent = job.sent;
          }
          if (job.status === 'completed') {
            toast.success(`Blast complete: ${job.sent} delivered, ${job.failed} failed of ${job.total}`, { id: `blast-${jobId}`, duration: 8000 });
            break;
          }
        } catch {
          // Network blip — keep polling
        }
      }
    } catch (err) {
      toast.error(err.message || 'Opt-in send failed');
    } finally {
      setOptInTplSending(false);
    }
  };

  const filteredForSend = sendMode === 'industry' && sendIndustry
    ? contacts.filter(c => (c.industry || '').toLowerCase() === sendIndustry.toLowerCase())
    : sendMode === 'selected' ? contacts : [];

  return (
    <div className="space-y-4">
      {/* Sender Health — deliverability snapshot, warns before blasting on risky reputation */}
      <SenderHealthCard />

      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{campaigns.length} campaigns</p>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="edit-optin-tpl-btn" size="sm" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" onClick={openOptInTpl}>
            <ShieldCheck size={16} className="mr-1" /> Edit Opt-In Template
          </Button>
          <Button data-testid="convert-blog-btn" size="sm" variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={openBlogPicker}>
            <FileText size={16} className="mr-1" /> Convert Blog → Email
          </Button>
          <Button data-testid="import-email-url-btn" size="sm" variant="outline" className="border-indigo-600 text-indigo-700 hover:bg-indigo-50" onClick={() => { setShowImport(true); setImportUrl(''); }}>
            <Link2 size={16} className="mr-1" /> Import from Email URL
          </Button>
          <Button data-testid="new-field-report-btn" size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={startNewFieldReport}>
            <Newspaper size={16} className="mr-1" /> New Field Report
          </Button>
          <Button data-testid="add-campaign-btn" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => { setEditing(null); setForm({ name: '', subject: '', content: '' }); setShowForm(true); }}>
            <Plus size={16} className="mr-1" /> New Campaign
          </Button>
        </div>
      </div>

      {showOptInTpl && (
        <Card className="border-amber-300 bg-amber-50/40" data-testid="optin-template-card">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2">
                <ShieldCheck size={18} className="text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-amber-900">Opt-In Email Template</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    This is the value-led email that goes out when you run the Opt-In campaign. Edits auto-save when you send a test or blast.
                    Placeholders: <code className="bg-white px-1 rounded">{`{OPT_IN_URL}`}</code>, <code className="bg-white px-1 rounded">{`{OPT_OUT_URL}`}</code>.
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowOptInTpl(false)} data-testid="optin-template-close"><X size={14} /></Button>
            </div>

            {optInTplLoading ? (
              <div className="text-center py-8"><Loader2 className="animate-spin inline" size={20} /> <span className="text-sm text-gray-500 ml-2">Loading template…</span></div>
            ) : (
              <>
                <input
                  data-testid="optin-tpl-subject"
                  value={optInTpl.subject}
                  onChange={e => setOptInTpl(t => ({ ...t, subject: e.target.value }))}
                  placeholder="Email Subject *"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none bg-white"
                />

                <div className="border rounded-lg bg-gray-50 overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
                    <span className="text-xs text-gray-600 font-semibold">Opt-In email body — full WYSIWYG editor</span>
                    <Button type="button" size="sm" variant="ghost" className="text-xs h-7" onClick={() => setOptInTplHtmlMode(s => !s)} data-testid="optin-tpl-toggle-html">
                      {optInTplHtmlMode ? 'Hide HTML' : 'Show HTML'}
                    </Button>
                  </div>
                  <RichTextEditor
                    testid="optin-tpl-editor"
                    value={optInTpl.content}
                    onChange={html => setOptInTpl(t => ({ ...t, content: html }))}
                  />
                </div>

                {optInTplHtmlMode && (
                  <textarea
                    data-testid="optin-tpl-html"
                    value={optInTpl.content}
                    onChange={e => setOptInTpl(t => ({ ...t, content: e.target.value }))}
                    placeholder="HTML Source"
                    rows={10}
                    className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-amber-500 focus:outline-none font-mono"
                  />
                )}

                {/* Recipient targeting (mode picker) */}
                <div className="border-t pt-3 space-y-2">
                  <div className="text-xs font-bold text-amber-900 uppercase tracking-wide flex items-center gap-1">
                    <Users size={12} /> Who should receive this opt-in?
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { mode: 'remaining', icon: MailQuestion, label: `Never Received (${(contacts || []).filter(c => !c.excluded && (!c.opt_in_status || c.opt_in_status === '')).length})`, highlight: true },
                      { mode: 'all',      icon: Users,       label: `All Active (${(contacts || []).filter(c => !c.excluded && (!c.opt_in_status || ['','pending'].includes(c.opt_in_status))).length})` },
                      { mode: 'industry', icon: Filter,      label: 'By Industry' },
                      { mode: 'selected', icon: CheckSquare, label: 'Select Contacts' },
                    ].map(opt => (
                      <button
                        key={opt.mode}
                        data-testid={`optin-mode-${opt.mode}`}
                        onClick={() => { setOptInSendMode(opt.mode); setOptInSelectedIds([]); ensureContactsLoadedForOptIn(); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded border text-xs font-medium transition-all ${
                          optInSendMode === opt.mode
                            ? (opt.highlight ? 'border-emerald-500 bg-emerald-100 text-emerald-900' : 'border-amber-500 bg-amber-100 text-amber-900')
                            : (opt.highlight ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300')
                        }`}
                      >
                        <opt.icon size={12} /> {opt.label}
                      </button>
                    ))}
                  </div>

                  {optInSendMode === 'remaining' && (
                    <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="optin-remaining-hint">
                      <strong>Safe re-send.</strong> Targets ONLY contacts with no opt-in status (—) — these never received the email due to the original send timeout. Anyone already "Pending" or "Opted In" will be skipped automatically.
                    </p>
                  )}

                  {optInSendMode === 'industry' && (
                    <select
                      data-testid="optin-industry-select"
                      value={optInIndustry}
                      onChange={e => setOptInIndustry(e.target.value)}
                      className="w-full px-3 py-2 border rounded text-sm bg-white focus:ring-2 focus:ring-amber-500 focus:outline-none"
                    >
                      <option value="">Choose an industry...</option>
                      {INDUSTRIES.map(i => {
                        const count = (contacts || []).filter(c => !c.excluded && (c.industry || '').toLowerCase() === i.toLowerCase()).length;
                        return <option key={i} value={i}>{i} ({count} contacts)</option>;
                      })}
                    </select>
                  )}

                  {optInSendMode === 'selected' && (
                    <>
                      <p className="text-xs text-gray-600">{optInSelectedIds.length} selected</p>
                      {loadingContacts ? (
                        <div className="text-center py-3"><Loader2 size={16} className="animate-spin inline" /></div>
                      ) : (
                        <div className="max-h-56 overflow-y-auto border rounded bg-white divide-y" data-testid="optin-contact-list">
                          {(contacts || []).filter(c => !c.excluded).map(c => (
                            <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={optInSelectedIds.includes(c.id)}
                                onChange={() => setOptInSelectedIds(prev => prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                                data-testid={`optin-pick-${c.id}`}
                              />
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-slate-900">{c.name || c.email}</span>
                                {c.name && <span className="text-xs text-gray-500 ml-2">{c.email}</span>}
                              </div>
                              {c.industry && <Badge className="bg-blue-50 text-blue-700 text-xs">{c.industry}</Badge>}
                            </label>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap pt-1">
                  <Button data-testid="optin-tpl-save" size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={handleSaveOptInTpl} disabled={optInTplSaving}>
                    {optInTplSaving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : <><Save size={14} className="mr-1" /> Save Template</>}
                  </Button>
                  <Button data-testid="optin-tpl-test" size="sm" variant="outline" className="border-blue-600 text-blue-700 hover:bg-blue-50" onClick={handleTestOptIn} disabled={optInTplSending || optInTplSaving}>
                    <Mail size={14} className="mr-1" /> Send Test
                  </Button>
                  <Button data-testid="optin-tpl-blast" size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSendOptInBlast} disabled={optInTplSending || optInTplSaving}>
                    {optInTplSending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</> : <><Send size={14} className="mr-1" /> Send Opt-In Blast</>}
                  </Button>
                  <Button data-testid="optin-tpl-reset" size="sm" variant="outline" className="border-gray-400 text-gray-600 hover:bg-gray-50 ml-auto" onClick={handleResetOptInTpl} disabled={optInTplSaving}>
                    <RotateCcw size={14} className="mr-1" /> Reset to Default
                  </Button>
                </div>

                {/* Sender health status + force-send checkbox */}
                <div className="border-t pt-3 mt-1 space-y-2" data-testid="optin-sender-health">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-amber-900 uppercase tracking-wide flex items-center gap-1">
                      <ShieldCheck size={12} /> Sender Health
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 text-xs text-gray-500" onClick={loadOptInHealth} disabled={optInHealthLoading} data-testid="optin-health-refresh">
                      {optInHealthLoading ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
                    </Button>
                  </div>
                  {optInHealth ? (
                    <div className={`rounded border px-3 py-2 text-xs ${
                      optInHealth.risk_level === 'critical' ? 'bg-rose-50 border-rose-300 text-rose-900'
                      : optInHealth.risk_level === 'warning' ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                    }`} data-testid={`optin-health-status-${optInHealth.risk_level}`}>
                      <div className="font-semibold flex items-center gap-1.5">
                        {optInHealth.risk_level === 'critical' && <span className="inline-block w-2 h-2 rounded-full bg-rose-500" />}
                        {optInHealth.risk_level === 'warning' && <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />}
                        {optInHealth.risk_level === 'good' && <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />}
                        Risk: {optInHealth.risk_level.toUpperCase()}
                        {optInHealth.sample_size != null && (
                          <span className="ml-2 text-[10px] opacity-70 font-normal">(sample: {optInHealth.sample_size} sends)</span>
                        )}
                      </div>
                      {(optInHealth.risk_reasons || []).length > 0 && (
                        <ul className="mt-1.5 ml-1 space-y-0.5 list-disc list-inside">
                          {optInHealth.risk_reasons.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                      )}
                      {optInHealth.risk_level === 'critical' && (
                        <label className="flex items-center gap-2 mt-2 pt-2 border-t border-rose-200 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={optInForceSend}
                            onChange={e => setOptInForceSend(e.target.checked)}
                            className="rounded border-rose-400 text-rose-600 focus:ring-rose-500"
                            data-testid="optin-force-send"
                          />
                          <span className="font-semibold">Override critical block (force send anyway)</span>
                        </label>
                      )}
                    </div>
                  ) : optInHealthLoading ? (
                    <div className="text-xs text-gray-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Checking sender reputation…</div>
                  ) : (
                    <div className="text-xs text-gray-500">Sender health unavailable. Click refresh to retry.</div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {showFromBlog && (
        <Card className="border-emerald-300 bg-emerald-50/40">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start gap-2">
              <FileText size={18} className="text-emerald-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm text-emerald-900">Convert a Blog Post into a Marketing Email</h4>
                <p className="text-xs text-gray-600 mt-1">
                  Pick any BCS blog post. Claude Sonnet 4.5 will condense it into an email-length piece
                  and wrap it in the BCS email template with fresh Unsplash images and your logo.
                </p>
              </div>
            </div>
            <select
              data-testid="convert-blog-select"
              value={selectedBlogSlug}
              onChange={e => setSelectedBlogSlug(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white"
            >
              <option value="">— pick a blog post —</option>
              {blogPosts.map(p => (
                <option key={p.slug} value={p.slug}>{p.title}</option>
              ))}
            </select>
            <div className="flex gap-2">
              <Button data-testid="convert-blog-fetch" size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={convertingBlog || !selectedBlogSlug} onClick={handleConvertFromBlog}>
                {convertingBlog ? <><Loader2 size={14} className="mr-1 animate-spin" /> Converting (15–30s)…</> : <><FileText size={14} className="mr-1" /> Convert to Email</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowFromBlog(false); setSelectedBlogSlug(''); }}>
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showImport && (
        <Card className="border-indigo-300 bg-indigo-50/40">
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start gap-2">
              <Link2 size={18} className="text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-semibold text-sm text-indigo-900">AI-Rebrand an Email as BCS Marketing</h4>
                <p className="text-xs text-gray-600 mt-1">
                  Paste any marketing email URL. Claude Sonnet 4.5 reads the source, identifies the topic, and writes
                  a <span className="font-semibold">brand new BCS-voiced email from scratch</span> on the same subject —
                  complete with a fresh Unsplash hero image, 3 body sections, BCS value bullets, and a BCS call-to-action.
                  Nothing from the source is copied verbatim.
                </p>
              </div>
            </div>
            <input
              data-testid="import-email-url-input"
              autoFocus
              value={importUrl}
              onChange={e => setImportUrl(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !importing) handleImportFromUrl(); }}
              placeholder="https://example-campaign-view.com/viewinbrowser?..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none"
            />
            <div className="flex gap-2">
              <Button data-testid="import-email-url-fetch" size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={importing} onClick={handleImportFromUrl}>
                {importing ? <><Loader2 size={14} className="mr-1 animate-spin" /> Rewriting (15–30s)…</> : <><Link2 size={14} className="mr-1" /> Rebrand as BCS</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setShowImport(false); setImportUrl(''); }}>
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
            <p className="text-xs text-gray-500 italic">
              Topic stays, voice becomes yours. You can still edit the output before saving or sending.
            </p>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="border-indigo-200">
          <CardContent className="pt-5">
            <div className="flex gap-2 mb-3">
              <span className="text-xs text-gray-500 pt-1">Templates:</span>
              {Object.entries(TEMPLATES).map(([k, v]) => (
                <Button key={k} type="button" variant="outline" size="sm" className="text-xs" onClick={() => applyTemplate(k)}>{v.name}</Button>
              ))}
            </div>
            <form onSubmit={handleSave} className="space-y-3">
              <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Campaign Name *" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
              <input required value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Email Subject *" className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />

              {/* Rich text editor with formatting toolbar */}
              <div className="border rounded-lg bg-gray-50 overflow-hidden">
                <div className="flex items-center justify-between px-3 py-2 bg-white border-b">
                  <span className="text-xs text-gray-600 font-semibold">Campaign body — full WYSIWYG editor</span>
                  <Button type="button" size="sm" variant="ghost" className="text-xs h-7" onClick={() => setShowHtmlSource(s => !s)}>
                    {showHtmlSource ? 'Hide HTML' : 'Show HTML'}
                  </Button>
                </div>
                <RichTextEditor
                  testid="campaign-editor"
                  value={form.content}
                  onChange={html => setForm(f => ({ ...f, content: html }))}
                />
              </div>

              {/* Swap Images Panel — extract Unsplash URLs from the email and let the user replace each one */}
              <ImageSwapPanel content={form.content} onReplace={(oldUrl, newUrl) => {
                setForm(f => ({ ...f, content: (f.content || '').split(oldUrl).join(newUrl) }));
              }} />

              {showHtmlSource && (
                <textarea
                  data-testid="campaign-content-html"
                  value={form.content}
                  onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                  placeholder="HTML Source (advanced)"
                  rows={8}
                  className="w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none font-mono"
                />
              )}

              <div className="flex gap-2">
                <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700"><Save size={16} className="mr-1" /> {editing ? 'Update' : 'Create'}</Button>
                <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(null); }}><X size={16} className="mr-1" /> Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Send Modal */}
      {sendModal && (
        <Card className="border-2 border-indigo-300 shadow-lg">
          <CardContent className="pt-5">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold text-slate-900 flex items-center gap-2"><Send size={18} /> Send Campaign</h4>
              <Button variant="ghost" size="sm" onClick={() => setSendModal(null)}><X size={16} /></Button>
            </div>

            {/* Mode Selection */}
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { mode: 'opted_in', icon: ShieldCheck, label: `Opted-In Only (${contacts.filter(c => c.opt_in_status === 'opted_in' && !c.excluded).length})`, highlight: true },
                { mode: 'all', icon: Users, label: `All Contacts (${contacts.length})` },
                { mode: 'industry', icon: Filter, label: 'By Industry' },
                { mode: 'selected', icon: CheckSquare, label: 'Select Manually' },
              ].map(opt => (
                <button
                  key={opt.mode}
                  data-testid={`send-mode-${opt.mode}`}
                  onClick={() => { setSendMode(opt.mode); setSelectedIds([]); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                    sendMode === opt.mode
                      ? (opt.highlight ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : 'border-indigo-500 bg-indigo-50 text-indigo-700')
                      : (opt.highlight ? 'border-emerald-300 bg-emerald-50/60 text-emerald-700 hover:bg-emerald-100' : 'border-gray-200 text-gray-600 hover:border-gray-300')
                  }`}
                >
                  <opt.icon size={16} /> {opt.label}
                </button>
              ))}
            </div>

            {sendMode === 'opted_in' && (
              <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 mb-3" data-testid="send-opted-in-hint">
                <strong>Recommended for marketing.</strong> Only contacts who explicitly opted in to your Field Report will receive this campaign — best deliverability, lowest spam risk.
              </p>
            )}

            {/* Industry Filter */}
            {sendMode === 'industry' && (
              <div className="mb-4">
                <select
                  data-testid="send-industry-select"
                  value={sendIndustry}
                  onChange={e => setSendIndustry(e.target.value)}
                  className="w-full px-3 py-2.5 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  <option value="">Choose an industry...</option>
                  {INDUSTRIES.map(i => {
                    const count = contacts.filter(c => (c.industry || '').toLowerCase() === i.toLowerCase()).length;
                    return <option key={i} value={i}>{i} ({count} contacts)</option>;
                  })}
                </select>
                {sendIndustry && (
                  <p className="text-sm text-indigo-600 mt-2 font-medium">
                    {contacts.filter(c => (c.industry || '').toLowerCase() === sendIndustry.toLowerCase()).length} contacts in {sendIndustry}
                  </p>
                )}
              </div>
            )}

            {/* Manual Selection */}
            {sendMode === 'selected' && (
              <div className="mb-4">
                {loadingContacts ? (
                  <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mx-auto" /></div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-2">{selectedIds.length} selected</p>
                    <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                      {contacts.filter(c => !c.excluded).map(c => (
                        <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(c.id)}
                            onChange={() => toggleSelect(c.id)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-900">{c.name || c.email}</span>
                            {c.name && <span className="text-xs text-gray-500 ml-2">{c.email}</span>}
                          </div>
                          {c.industry && <Badge className="bg-blue-50 text-blue-700 text-xs">{c.industry}</Badge>}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-2 pt-2 border-t">
              <Button
                data-testid="confirm-send-btn"
                onClick={handleSend}
                disabled={sending}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Send size={16} className="mr-2" /> {sending ? 'Sending...' : 'Send Campaign'}
              </Button>
              <Button variant="outline" onClick={() => setSendModal(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto" /></div>
      : campaigns.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400"><Mail className="mx-auto mb-2" size={32} /><p>No campaigns yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <Card key={c.id} className="hover:shadow-sm">
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900 truncate">{c.name}</h4>
                    <Badge className={c.status === 'sent' ? 'bg-emerald-100 text-emerald-800' : c.status === 'draft' ? 'bg-gray-100 text-gray-700' : 'bg-amber-100 text-amber-800'}>{c.status}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{c.subject}</p>
                  {c.status === 'sent' && <p className="text-xs text-gray-400 mt-1">Sent to {c.sent_count} recipients</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {c.status === 'draft' && (
                    <>
                      <Button variant="outline" size="sm" className="text-blue-600" onClick={() => handleTestEmail(c.id)} data-testid={`test-email-${c.id}`}><Mail size={14} className="mr-1" /> Test</Button>
                      <Button variant="outline" size="sm" onClick={() => openSendModal(c.id)} data-testid={`send-campaign-${c.id}`}><Send size={14} className="mr-1" /> Send</Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(c.id); setForm({ name: c.name, subject: c.subject, content: c.content || '' }); setShowForm(true); }}><Edit2 size={14} /></Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(c.id)}><Trash2 size={14} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CRMCampaigns;
